// Kleine cache + prefetch-helper voor pagina's die per week data ophalen.
//
// Aanleiding (wiki-feedback): "de volgende week is nog niet als buffer geladen, hierdoor
// duurt het ontzettend lang voordat de volgende pagina is geladen". Elke klik op ‹ of ›
// deed een volledige round-trip naar Airtable. We houden opgehaalde weken kort vast en
// laden de buurweken alvast op de achtergrond, zodat doorklikken direct voelt.
//
// Bewust per pagina-instantie (useRef) en met een korte TTL: de planning verandert door
// collega's, dus een cache die een sessie lang meegaat zou verouderde uren tonen.

const STANDAARD_TTL_MS = 2 * 60 * 1000;

export function createWeekCache({ ttlMs = STANDAARD_TTL_MS, maxEntries = 12 } = {}) {
  const store = new Map(); // key -> { waarde, ts }
  const lopend = new Map(); // key -> Promise (voorkomt dubbele fetch van dezelfde week)
  // Sleutels die ongeldig werden terwijl er al een lading voor onderweg was. Die lading
  // is vóór de wijziging gestart en mag dus niet alsnog in de cache belanden — anders
  // legt een prefetch die net vertrok precies de waarde vast die je zojuist veranderde.
  const vervallen = new Set();

  const geldig = (entry) => entry && Date.now() - entry.ts < ttlMs;

  return {
    /** Waarde uit de cache, of undefined als er niets (vers) is. */
    get(key) {
      const entry = store.get(key);
      if (!geldig(entry)) {
        if (entry) store.delete(key);
        return undefined;
      }
      return entry.waarde;
    },

    set(key, waarde) {
      store.set(key, { waarde, ts: Date.now() });
      // Simpele FIFO-begrenzing: Map bewaart invoegvolgorde.
      while (store.size > maxEntries) {
        store.delete(store.keys().next().value);
      }
    },

    /**
     * Haal op via `loader(key)` — uit de cache als het kan, anders één gedeelde fetch.
     * Gooit door wat de loader gooit; mislukte pogingen worden niet gecachet.
     */
    async load(key, loader) {
      const gecachet = this.get(key);
      if (gecachet !== undefined) return gecachet;
      if (lopend.has(key)) return lopend.get(key);

      const promise = (async () => {
        // Deze lading start nú; alleen een `delete` die híerna komt maakt hem ongeldig.
        vervallen.delete(key);
        try {
          const waarde = await loader(key);
          if (!vervallen.has(key)) this.set(key, waarde);
          return waarde;
        } finally {
          lopend.delete(key);
          vervallen.delete(key);
        }
      })();
      lopend.set(key, promise);
      return promise;
    },

    /** Laad op de achtergrond; fouten zijn hier niet interessant (de gebruiker wacht niet). */
    prefetch(key, loader) {
      if (this.get(key) !== undefined || lopend.has(key)) return;
      this.load(key, loader).catch(() => {});
    },

    /**
     * Eén sleutel vergeten — voor als je zélf iets wijzigt dat in die periode valt.
     *
     * Dat is iets anders dan `clear()`: alles weggooien betekent dat je ná elke
     * verplaatsing het hele scherm opnieuw ophaalt, inclusief de periodes waar niets aan
     * veranderde. Een lading die al onderweg is voor deze sleutel landt niet meer in de
     * cache; hij vertrok immers vóór de wijziging.
     */
    delete(key) {
      store.delete(key);
      if (lopend.has(key)) vervallen.add(key);
    },

    clear() {
      store.clear();
      lopend.clear();
      vervallen.clear();
    },
  };
}
